<script lang="ts" setup>
import { RoutesEnum } from '~/enums'
import type { Swiper as SwiperType } from 'swiper/types'
import { useSwiper } from '~/composables/common/use-swiper'
import { useLiveCasinoFilter } from '~/composables/game/use-live-casino-filter'

const {
  providerTabs,
  changeCategory,
  onClickProvider,
  getProviderImage,
  categoryDisplays,
  currentCategory,
  currentProvider,
} = useLiveCasinoFilter()

const { modules, SwiperSlide, Swiper } = useSwiper()

const providerRef = ref()
const categoryRef = ref()
const liveCasinoRefFilter = ref<HTMLDivElement | null>(null)

const setInitialSlide = () => {
  const providerIndex = providerTabs.value?.findIndex(
    (item) => item.provider === currentProvider.value,
  )
  const categoryIndex = categoryDisplays.value?.findIndex(
    (item) => item.slug === currentCategory.value,
  )
  providerRef.value?.slideTo(providerIndex - 3, 300, false)
  categoryRef.value?.slideTo(categoryIndex - 3, 300, false)
}

onMounted(() => {
  nextTick(() => setInitialSlide())
})

const onSwiperCategories = (swiper: SwiperType): void => {
  categoryRef.value = swiper
  nextTick(() => setInitialSlide())
}

const onSwiperProviders = (swiper: SwiperType): void => {
  providerRef.value = swiper
  nextTick(() => setInitialSlide())
}

const clickCategoryTab = (category: string, index: number): void => {
  changeCategory(category)
  const slideIndex = Math.max(index - 3, 0)
  categoryRef.value.slideTo(slideIndex, 300, false)
}

watch(
  currentProvider || currentCategory,
  () => {
    setTimeout(() => {
      nextTick(() => setInitialSlide())
    }, 0)
  },
  { immediate: true, deep: true },
)
</script>
<template>
  <div class="live-casino container">
    <div class="live-casino__banner">
      <img
        class="live-casino__banner-img"
        src="/images/live-casino/banner-live-casino.webp"
        alt="Banner Casino"
        width="1000"
        height="145"
      />
      <h2 class="live-casino__title">Live Casino</h2>
      <p class="live-casino__description">Dealer sexy - Bàn chơi đa dạng</p>
    </div>
    <CommonCasinoLivestream />

    <div ref="liveCasinoRefFilter" class="live-casino__filter-game">
      <div
        class="live-casino__providers"
        :class="{ 'provider-init': providerRef }"
      >
        <div v-show="providerRef" class="swiper-arrow">
          <div class="swiper-arrow__prev swiper-button-prev">
            <span class="icon-arrow icon-left" />
          </div>
          <div class="swiper-arrow__next swiper-button-next">
            <span class="icon-arrow icon-right" />
          </div>
        </div>
        <Swiper
          :modules="modules"
          :slides-per-view="'auto'"
          :space-between="8"
          :navigation="{
            nextEl: '.swiper-arrow__next',
            prevEl: '.swiper-arrow__prev',
          }"
          @swiper="onSwiperProviders"
        >
          <SwiperSlide
            v-for="providerTab in providerTabs"
            :key="providerTab.tabId"
          >
            <div
              class="cursor-pointer"
              :class="[
                'live-casino__provider',
                {
                  'live-casino__provider--selected':
                    providerTab.provider === currentProvider,
                },
              ]"
              @click.prevent="onClickProvider(providerTab.provider)"
            >
              <a
                :href="`${RoutesEnum.LiveCasino}/${providerTab.provider}`"
                class="pointer-events-none provider-link-wrapper"
              >
                <img
                  class="live-casino__provider__image"
                  :src="
                  getProviderImage(
                    providerTab.provider === currentProvider
                      ? `${providerTab.icon}-active`
                      : providerTab.icon!,
                  )
                "
                  :alt="providerTab.title"
                  width="32"
                  height="32"
                />
                <div class="live-casino__provider__title" rounded>
                  {{ providerTab.title }}
                </div>
              </a>
            </div>
          </SwiperSlide>
        </Swiper>
      </div>
      <div class="live-casino__categories">
        <span class="live-casino__categories__title text-no-wrap"
          >Thể loại</span
        >
        <div class="live-casino__categories__content">
          <Swiper
            v-if="categoryDisplays?.length"
            :modules="modules"
            :slides-per-view="'auto'"
            :space-between="0"
            @swiper="onSwiperCategories"
          >
            <SwiperSlide
              v-for="(category, index) in categoryDisplays"
              :key="category.slug"
              :value="category.slug"
            >
              <div
                class="cursor-pointer live-casino__categories__item"
                @click.prevent="clickCategoryTab(category.slug, index)"
              >
                <a
                  :href="`${RoutesEnum.LiveCasino}/${currentProvider}/${category.slug}`"
                  class="pointer-events-none v-btn v-btn--elevated v-theme--light v-btn--density-default v-btn--rounded v-btn--size-default v-btn--variant-elevated"
                  :class="{
                    'v-btn--selected': category.slug === currentCategory,
                  }"
                >
                  <span class="v-btn__content">
                    {{ category.name }}
                  </span>
                </a>
              </div>
            </SwiperSlide>
          </Swiper>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
@import '/assets/styles/components/desktop/pages/live-casino/filter.scss';
</style>
